import { useQuery } from 'react-query';
import { AxiosResponse } from 'axios';
import apiClient from '../apiClient/apiClient';
import {
  Dashboard, MenuTranslationFailures, NullSpecsData, FailedExtracts, EventTranslationFailures, NegativePriceData
} from './dashboard.types';
import { Organization } from '../menu/menu.types';

export const useDashboard = (requestParams: {
  dayFilter: string,
  from: string | null | undefined,
  to: string | null | undefined
}) => {
  return useQuery<AxiosResponse<Dashboard>>({
    queryKey: ['dashboard', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<Dashboard>('/dashboard', { params: {from: requestParams.from, to: requestParams.to } }),
    enabled: true,
    cacheTime: 0,
    refetchInterval: requestParams.dayFilter === 'TODAY' ? 12000 : false
  });
};

export const useNullSpecsDetails = (
  requestParams: {
    from: string | null | undefined,
    to: string | null | undefined
  },
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<NullSpecsData[]>>({
    queryKey: ['nullSpecMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<NullSpecsData[]>('/dashboard/missing-specs', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useExtractionsAndActivationsDetails = (
  jobId: string,
  extractType: string,
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<FailedExtracts[]>>({
    queryKey: ['extractions-and-activations-details', jobId, extractType],
    queryFn: () => apiClient.get<FailedExtracts[]>(`/dashboard/failed-extracts/${jobId}?extractType=${extractType}`),
    enabled
  })
};

export const useMenu99PricingDetails = (
  requestParams: {
    from: string | null | undefined,
    to: string | null | undefined
  },
  enabled: boolean = true
) => {
  return useQuery<AxiosResponse<Organization[]>>({
    queryKey: ['menuPricingMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<Organization[]>('/dashboard/menus-99-pricing', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useMenuBundlePricingDetails = (
  requestParams: {
    from: string | null | undefined,
    to: string | null | undefined
  },
  enabled: boolean = true
) => {
  return useQuery<AxiosResponse<Organization[]>>({ 
    queryKey: ['menuBundlePricingMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<Organization[]>('/dashboard/bundles-99-pricing', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
}

export const useMenuNegativePricingDetails = (
  requestParams: {
    from: string | null | undefined,
    to: string | null | undefined
  },
  enabled: boolean = true
) => {
  return useQuery<AxiosResponse<NegativePriceData[]>>({
    queryKey: ['menuNegativePricingMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<NegativePriceData[]>('/dashboard/negative-prices', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
}

export const useTranslationFailures = (
        requestParams: {
          from: string | null | undefined,
          to: string | null | undefined
        },
        enabled: boolean = true
) => {
  return useQuery<AxiosResponse<MenuTranslationFailures[]>>({
    queryKey: ['menuTranslationFailures', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<MenuTranslationFailures[]>('/dashboard/failed-translations', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useCouponTranslationFailures = (
        requestParams: {
          from: string | null | undefined,
          to: string | null | undefined
        },
        enabled: boolean = true
) => {
  return useQuery<AxiosResponse<MenuTranslationFailures[]>>({
    queryKey: ['couponTranslationFailures', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<MenuTranslationFailures[]>('/dashboard/failed-coupons', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useEventTranslationFailures = (
        requestParams: {
          failed: boolean,
          from: string | null | undefined,
          to: string | null | undefined
        },
        enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationFailures[]>>({
    queryKey: ['eventTranslationFailures', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<EventTranslationFailures[]>('/dashboard/translations', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
};